import { useCallback, useEffect, useState } from 'react'
import { getDataEntry } from '@/lib/linkBuilderApi'
import {
  projectDefaultBaseUrl,
  projectPresets,
  projectTagDefinitions,
  type Preset,
  type TagDefinition,
} from '@/lib/projectDataEntry'

export type { Preset, TagDefinition }

export type DataEntryState =
  | { status: 'loading' }
  | {
      status: 'ready'
      defaultBaseUrl: string | null
      tagDefinitions: TagDefinition[]
      presets: Preset[]
    }
  | { status: 'no-token' }
  | { status: 'error' }

export type TagDefinitionsState =
  | { status: 'loading' }
  | { status: 'ready'; definitions: TagDefinition[] }
  | { status: 'error' }

export function toTagDefinitionsState(state: DataEntryState): TagDefinitionsState {
  if (state.status === 'ready') return { status: 'ready', definitions: state.tagDefinitions }
  if (state.status === 'loading') return { status: 'loading' }
  return { status: 'error' }
}

interface UseDataEntryArgs {
  getToken: () => Promise<string | null>
  subscriptionCode: string
}

export interface UseDataEntryResult {
  state: DataEntryState
  refetch: () => void
}

export function useLinkBuilderDataEntry({
  getToken,
  subscriptionCode,
}: UseDataEntryArgs): UseDataEntryResult {
  const [state, setState] = useState<DataEntryState>({ status: 'loading' })
  // Bumping this token forces the effect below to refire.
  const [refetchToken, setRefetchToken] = useState(0)

  useEffect(() => {
    const controller = new AbortController()
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reset on subscription change; alternative would be stale data flash
    setState({ status: 'loading' })
    ;(async () => {
      try {
        const token = await getToken()
        if (controller.signal.aborted) return
        if (token === null) {
          setState({ status: 'no-token' })
          return
        }
        const res = await getDataEntry({
          token,
          subscriptionCode,
          signal: controller.signal,
        })
        if (controller.signal.aborted) return
        setState({
          status: 'ready',
          defaultBaseUrl: projectDefaultBaseUrl(res.defaultBaseUrl),
          tagDefinitions: projectTagDefinitions(res.tagDefinitions ?? []),
          presets: projectPresets(res.presets ?? []),
        })
      } catch (err: unknown) {
        if (controller.signal.aborted) return
        if (err instanceof DOMException && err.name === 'AbortError') return
        setState({ status: 'error' })
      }
    })()
    return () => controller.abort()
  }, [getToken, subscriptionCode, refetchToken])

  const refetch = useCallback(() => setRefetchToken((t) => t + 1), [])

  return { state, refetch }
}
